import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader, useMantineTheme } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { IconArrowLeft, IconChevronRight } from "@tabler/icons-react";
import ReactECharts from "echarts-for-react";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { NON_GOAL_BUCKETS } from "utils/strategicGoals";
import css from "./OrganizationStrategicFocus.module.scss";

const COLORS = [
  "#39a8f0",
  "#63bca4",
  "#d9ae58",
  "#b39ade",
  "#e59292",
  "#76b8c9",
];
const fallbackColor = (name) => {
  let hash = 0;
  for (const character of name)
    hash = ((hash << 5) - hash + character.charCodeAt(0)) | 0;
  return COLORS[Math.abs(hash) % COLORS.length];
};
const meetingLabel = (count) =>
  `${count.toLocaleString()} ${count === 1 ? "meeting" : "meetings"}`;
const percentage = (count, total) => {
  const value = total ? (count / total) * 100 : 0;
  return value > 0 && value < 1 ? "<1%" : `${Math.round(value)}%`;
};

export default function OrganizationStrategicFocus({
  goals,
  isLoading,
  isError,
  duration,
  self,
  uid,
  collection = "orgCalculations",
}) {
  const theme = useMantineTheme();
  const reducedMotion = useReducedMotion();
  const { openDrilldown } = useInsightsDrilldown();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hoveredName, setHoveredName] = useState(null);
  const chartRef = useRef(null);
  const listRef = useRef(null);
  const backRef = useRef(null);
  const lastCategory = useRef(null);

  const categories = useMemo(() => {
    const groups = new Map();
    Object.entries(goals || {}).forEach(([name, info]) => {
      if (!Number.isFinite(info?.count) || info.count <= 0) return;
      const category = info.category || "Uncategorized";
      if (!groups.has(category)) {
        groups.set(category, {
          name: category,
          value: 0,
          color:
            category === "Others"
              ? "#c4ccd6"
              : category === "No goal"
                ? "#7d8796"
                : theme.colors[info.categoryColor?.toLowerCase()]?.[6] ||
                  fallbackColor(category),
          goals: [],
        });
      }
      const group = groups.get(category);
      group.value += info.count;
      group.goals.push({ name, value: info.count });
    });
    return Array.from(groups.values())
      .sort((a, b) => b.value - a.value)
      .map((group) => ({
        ...group,
        goals: group.goals
          .sort((a, b) => b.value - a.value)
          .map((goal, index) => ({
            ...goal,
            color:
              group.goals.length === 1
                ? group.color
                : COLORS[index % COLORS.length],
          })),
      }));
  }, [goals, theme.colors]);
  const category = categories.find((item) => item.name === selectedCategory);
  const rows = category ? category.goals : categories;
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const hasGoals = categories.some((group) =>
    group.goals.some((goal) => !NON_GOAL_BUCKETS.includes(goal.name)),
  );
  const canOpenMeetings = Boolean(uid && duration && self);

  useEffect(() => {
    setSelectedCategory(null);
  }, [uid, duration, self]);
  useEffect(() => {
    if (selectedCategory && !category) setSelectedCategory(null);
  }, [selectedCategory, category]);
  useEffect(() => {
    setHoveredName(null);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [selectedCategory, uid, duration, self]);

  const clearChartHover = () => {
    const chart = chartRef.current?.getEchartsInstance();
    chart?.dispatchAction({ type: "hideTip" });
    chart?.dispatchAction({ type: "downplay", seriesIndex: 0 });
    setHoveredName(null);
  };

  const activate = (name) => {
    if (!rows.some((row) => row.name === name)) return;
    clearChartHover();
    if (!category) {
      lastCategory.current = name;
      setSelectedCategory(name);
    } else if (canOpenMeetings) {
      openDrilldown({
        name,
        type: "Goals",
        redisKey: `users:${uid}:${collection}:strategicGoals:${duration}:${self}:${name}`,
      });
    }
  };
  const clickRef = useRef(activate);
  clickRef.current = activate;
  const events = useMemo(
    () => ({
      click: (params) => clickRef.current(params.name),
      mouseover: (params) => setHoveredName(params.name),
      mouseout: () => setHoveredName(null),
    }),
    [],
  );
  const highlight = (name) => {
    setHoveredName(name);
    const chart = chartRef.current?.getEchartsInstance();
    chart?.dispatchAction({ type: "downplay", seriesIndex: 0 });
    if (name)
      chart?.dispatchAction({ type: "highlight", seriesIndex: 0, name });
  };
  const option = useMemo(
    () => ({
      animation: !reducedMotion,
      animationDuration: 450,
      animationDurationUpdate: 550,
      animationEasingUpdate: "cubicInOut",
      tooltip: {
        trigger: "item",
        renderMode: "richText",
        hideDelay: 0,
        formatter: (item) => {
          if (
            !Number.isFinite(item?.value) ||
            !rows.some((row) => row.name === item.name)
          )
            return "";
          return `${item.name}\n${meetingLabel(item.value)} · ${percentage(item.value, total)} ${category ? "of category" : "of all meetings"}`;
        },
      },
      series: [
        {
          id: "strategic-focus",
          type: "pie",
          universalTransition: !reducedMotion,
          animationTypeUpdate: "transition",
          radius: ["66%", "86%"],
          label: { show: false },
          labelLine: { show: false },
          showZeroSum: false,
          itemStyle: { borderWidth: 2, borderColor: "#fff", borderRadius: 2 },
          emphasis: { scaleSize: 4 },
          cursor: !category || canOpenMeetings ? "pointer" : "default",
          data: rows.map((row) => ({
            id: JSON.stringify([category?.name || null, row.name]),
            groupId: category ? category.name : row.name,
            name: row.name,
            value: row.value,
            itemStyle: { color: row.color },
          })),
        },
      ],
    }),
    [reducedMotion, rows, total, category, canOpenMeetings],
  );

  return (
    <section className={css.card} aria-label="Strategic focus">
      <header className={css.header}>
        <h3>Strategic focus</h3>
        <span>By meetings</span>
      </header>
      {isLoading ? (
        <div className={css.state} role="status">
          <Loader size="sm" />
          Fetching goal split
        </div>
      ) : isError ? (
        <div className={css.state} role="alert">
          Unable to load strategic focus
        </div>
      ) : !hasGoals ? (
        <div className={css.state}>
          No strategic goals found for this period
        </div>
      ) : (
        <>
          {category && (
            <div className={css.navigation}>
              <button
                ref={backRef}
                type="button"
                onClick={() => {
                  clearChartHover();
                  setSelectedCategory(null);
                  requestAnimationFrame(() => {
                    const buttons = listRef.current?.querySelectorAll("button");
                    Array.from(buttons || [])
                      .find(
                        (button) =>
                          button.dataset.name === lastCategory.current,
                      )
                      ?.focus();
                  });
                }}
              >
                <IconArrowLeft size={14} />
                Back to categories
              </button>
              <span title={category.name}>{category.name}</span>
            </div>
          )}
          <div className={css.content}>
            <div className={css.ring}>
              <ReactECharts
                key={JSON.stringify([uid, duration, self])}
                ref={chartRef}
                className={css.ringChart}
                option={option}
                onEvents={events}
                opts={{ renderer: "svg" }}
                style={{ height: "100%", width: "100%" }}
              />
              <div className={css.ringLabel} aria-live="polite">
                <strong>{total.toLocaleString()}</strong>
                <span>{total === 1 ? "meeting" : "meetings"}</span>
                <small>
                  {category ? "in this category" : "across all categories"}
                </small>
              </div>
            </div>
            <div className={css.listColumn}>
              <div className={css.listHeader}>
                <span>{category ? "Strategic goals" : "Categories"}</span>
                <span>{category ? "% of category" : "% of all meetings"}</span>
              </div>
              <div className={css.list} ref={listRef}>
                {rows.map((row) => (
                  <button
                    key={row.name}
                    type="button"
                    data-name={row.name}
                    className={`${css.row} ${hoveredName === row.name ? css.highlighted : ""}`}
                    disabled={Boolean(category) && !canOpenMeetings}
                    aria-label={`${row.name}, ${meetingLabel(row.value)}, ${percentage(row.value, total)}. ${category ? "View meetings" : "View strategic goals"}`}
                    onClick={() => {
                      activate(row.name);
                      if (!category)
                        requestAnimationFrame(() => backRef.current?.focus());
                    }}
                    onMouseEnter={() => highlight(row.name)}
                    onMouseLeave={() => highlight(null)}
                    onFocus={() => highlight(row.name)}
                    onBlur={() => highlight(null)}
                  >
                    <span
                      className={css.swatch}
                      style={{ background: row.color }}
                    />
                    <span className={css.name}>{row.name}</span>
                    <span className={css.percent}>
                      {percentage(row.value, total)}
                    </span>
                    <strong>{row.value.toLocaleString()}</strong>
                    <IconChevronRight size={14} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className={css.hint}>
            {category
              ? "Select a goal to see its meetings."
              : "Select a category to explore its strategic goals."}
          </p>
        </>
      )}
    </section>
  );
}
